import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { selectionKey, formatPhoneWithExtension } from "./data";
import { colors } from "../../utils/styles";

// ---------- Constants ----------

const PAGE_MARGIN = 10;

// jsPDF places the text baseline at y, so we shift section headers down by ~the
// cap height of a 12pt font (≈9pt) to make the surrounding gap visible above
// the glyphs rather than swallowed by the baseline offset.
const HEADER_BASELINE_OFFSET = 7;

const TABLE_DEFAULTS = {
  theme: "grid",
  headStyles: { fillColor: colors.navy, textColor: colors.white, fontSize: 9 },
  styles: { fontSize: 9, cellPadding: 4, textColor: colors.text },
  margin: { left: PAGE_MARGIN, right: PAGE_MARGIN }
};

const SUBAGENCY_COLUMNS = [
  { header: "Subagency", get: (entry, label) => label, width: 56 },
  { header: "Account Type", get: (entry) => entry.accountType, width: 34 },
  { header: "Active", get: (entry) => formatYesNo(entry.active), width: 26 },
  { header: "Desc Update", get: (entry) => formatYesNo(entry.reqDescUpdate), width: 30 },
  {
    header: "New Tax Bill Description",
    get: (entry) => (entry.reqDescUpdate ? entry.newDescription || "" : ""),
    width: 50
  },
  { header: "Charge Status", get: (entry) => entry.chargeStatus, width: 34 },
  { header: "Charge Category", get: (entry) => entry.chargeCategory, width: 45 },
  { header: "Service Description", get: (entry) => entry.serviceDesc, width: 55 },
  { header: "Est. Parcels", get: (entry) => formatInteger(entry.estParcels), width: 34 },
  { header: "Est. Amount", get: (entry) => formatCurrency(entry.estAmount), width: 50 },
  { header: "Enabling Statute", get: (entry) => entry.enablingStatute, width: 45 },
  { header: "Legal Authority Type", get: (entry) => entry.legalAuthorityType, width: 45 },
  { header: "Approval Method", get: (entry) => entry.approvalMethod, width: 37 },
  { header: "Election Date", get: (entry) => entry.electionDate || "", width: 41 },
  { header: "Approval Date", get: (entry) => entry.approvalDate || "", width: 41 },
  { header: "Adoption Type", get: (entry) => entry.adoptionType || "", width: 37 },
  { header: "Adoption Date", get: (entry) => entry.adoptionDate || "", width: 41 },
  { header: "Orig. Adoption Date", get: (entry) => entry.origAdoptionDate || "", width: 41 },
  { header: "Legal In Place", get: (entry) => formatYesNo(entry.legalInPlace), width: 30 }
];

// ---------- Formatters ----------

function formatYesNo(value) {
  if (value === "Yes" || value === true) return "Yes";
  if (value === "No" || value === false) return "No";
  return "";
}

function formatCurrency(raw) {
  if (raw == null || raw === "") return "";
  const cleaned = String(raw).replace(/[$,\s]/g, "");
  const value = parseFloat(cleaned);
  if (!Number.isFinite(value)) return String(raw);
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function formatInteger(raw) {
  if (raw == null || raw === "") return "";
  const cleaned = String(raw).replace(/[,\s]/g, "");
  const value = parseInt(cleaned, 10);
  if (!Number.isFinite(value)) return String(raw);
  return value.toLocaleString("en-US");
}

// ---------- Resolvers / Collectors ----------

function resolveAgencyDisplay(selection, parentOptions) {
  if (!selection) return "";
  const key = selectionKey(selection);
  const option = parentOptions.find((o) => o.key === key);
  return option?.label || "";
}

function resolveSubagencyDisplay(entry, subagenciesBySelection, selection) {
  const subs = subagenciesBySelection[selectionKey(selection)] || [];
  const sub = subs.find((s) => s.code === entry.subagency);
  if (!sub) return entry.subagency || "";
  return `${sub.name} (${sub.code})`;
}

function collectContactEmails(entries, contactsByCode) {
  const seen = new Set();
  const ordered = [];
  entries.forEach((entry) => {
    if (!entry.subagency) return;
    const list = contactsByCode[entry.subagency] || [];
    list.forEach((contact) => {
      const email = (contact.email || "").trim();
      if (!email) return;
      const key = email.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      ordered.push(email);
    });
  });
  return ordered;
}

// ---------- Renderers ----------

function renderHeader(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(colors.navy);
  doc.rect(0, 0, pageWidth, 60, "F");
  doc.setFillColor(colors.gold);
  doc.rect(0, 60, pageWidth, 3, "F");

  doc.setTextColor(colors.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("Preliminary Notification of Direct Charges", PAGE_MARGIN + 20, 28);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    "San Bernardino County — Auditor-Controller/Treasurer/Tax Collector — Property Tax Division",
    PAGE_MARGIN + 20,
    42
  );
}

function renderFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(colors.textMuted);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - PAGE_MARGIN, pageHeight - 20, { align: "right" });
  }
}

function renderSectionHeader(doc, label, cursorY) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(colors.navy);
  doc.text(label, PAGE_MARGIN, cursorY + HEADER_BASELINE_OFFSET);
  return cursorY + HEADER_BASELINE_OFFSET;
}

function renderSubagencyTable(doc, filledEntries, subagenciesBySelection, selection, startY) {
  const head = [SUBAGENCY_COLUMNS.map((c) => c.header)];
  const body = filledEntries.map((entry) => {
    const label = resolveSubagencyDisplay(entry, subagenciesBySelection, selection);
    return SUBAGENCY_COLUMNS.map((c) => c.get(entry, label) ?? "");
  });

  const columnStyles = Object.fromEntries(
    SUBAGENCY_COLUMNS.map((c, index) => [index, { cellWidth: c.width }])
  );

  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY,
    head,
    body,
    headStyles: { ...TABLE_DEFAULTS.headStyles, fontSize: 7, cellPadding: 3, valign: "middle" },
    styles: {
      ...TABLE_DEFAULTS.styles,
      fontSize: 7,
      cellPadding: 3,
      valign: "top",
      overflow: "linebreak",
      lineColor: colors.border
    },
    alternateRowStyles: { fillColor: colors.sectionBg },
    columnStyles
  });

  return doc.lastAutoTable.finalY;
}

// ---------- Main builder ----------

function buildSubmissionPdf({
  selection,
  parentOptions = [],
  subagenciesBySelection = {},
  entries = [],
  signers = [],
  preparer = {},
  confirmRecipients = {},
  contactsByCode = {},
  userEmail = "",
  taxRollYear = ""
}) {
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });

  renderHeader(doc);

  let cursorY = 75; // Start below header and gap

  const summaryRows = [["Tax Roll Year", taxRollYear || ""]];
  if (userEmail) summaryRows.push(["Submitted By", userEmail]);
  summaryRows.push(["Parent Agency", resolveAgencyDisplay(selection, parentOptions) || "—"]);

  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: cursorY,
    head: [[{ content: "Submission Summary", colSpan: 2 }]],
    body: summaryRows,
    columnStyles: {
      0: { cellWidth: 140, fontStyle: "bold", textColor: colors.textMuted },
      1: { cellWidth: "auto" }
    }
  });
  cursorY = doc.lastAutoTable.finalY + 15;

  const filledEntries = entries.filter((e) => e.subagency);
  cursorY = renderSectionHeader(doc, `Direct Charges (${filledEntries.length})`, cursorY) + 8;

  if (filledEntries.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(colors.textMuted);
    cursorY += 12;
    doc.text("No direct charges entered.", PAGE_MARGIN, cursorY);
    cursorY += 12;
  } else {
    cursorY = renderSubagencyTable(doc, filledEntries, subagenciesBySelection, selection, cursorY) + 15;
  }

  cursorY = renderSectionHeader(doc, "Authorized Signer(s)", cursorY);

  const signerBody =
    signers.length === 0
      ? [["—", "—", "—", "—"]]
      : signers.map((s) => [
          s.name || "",
          s.title || "",
          formatPhoneWithExtension(s.phone, s.phoneExtension),
          s.email || ""
        ]);

  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: cursorY + 8,
    head: [["Name", "Title", "Phone", "Email"]],
    body: signerBody
  });
  cursorY = doc.lastAutoTable.finalY + 15;

  cursorY = renderSectionHeader(doc, "Form Preparer & Certification", cursorY);

  const preparerName = `${preparer.first || ""} ${preparer.last || ""}`.trim() || "—";

  autoTable(doc, {
    ...TABLE_DEFAULTS,
    startY: cursorY + 8,
    head: [["Preparer Name", "Preparer Phone", "Preparer Email"]],
    body: [
      [preparerName, formatPhoneWithExtension(preparer.phone, preparer.phoneExtension) || "—", preparer.email || "—"]
    ]
  });
  cursorY = doc.lastAutoTable.finalY + 10;

  const recipientColumns = [];
  if (confirmRecipients.preparer) {
    const email = (preparer.email || "").trim();
    recipientColumns.push({ header: "Form Preparer", emails: email ? [email] : [] });
  }
  if (confirmRecipients.signer) {
    const signerEmails = signers.map((s) => (s.email || "").trim()).filter(Boolean);
    recipientColumns.push({ header: "Authorized Signer", emails: signerEmails });
  }
  if (confirmRecipients.contacts) {
    const contactEmails = collectContactEmails(entries, contactsByCode);
    recipientColumns.push({ header: "Contacts", emails: contactEmails });
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(colors.text);
  cursorY += 6;
  doc.text("Confirmation Sent To:", PAGE_MARGIN, cursorY);
  cursorY += 6;

  if (recipientColumns.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(colors.textMuted);
    doc.text("No confirmation recipients selected.", PAGE_MARGIN, cursorY + 8);
  } else {
    autoTable(doc, {
      ...TABLE_DEFAULTS,
      startY: cursorY,
      head: [recipientColumns.map((c) => c.header)],
      body: [recipientColumns.map((c) => (c.emails.length ? c.emails.join("\n") : "—"))],
      styles: { ...TABLE_DEFAULTS.styles, overflow: "linebreak", valign: "top" }
    });
  }

  renderFooter(doc);

  return doc;
}

// ---------- Public exports ----------

export function buildSubmissionPdfBase64(args) {
  const doc = buildSubmissionPdf(args);
  const bytes = new Uint8Array(doc.output("arraybuffer"));
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

// Tracks the most recent preview URL so rapid clicks don't pile up Blob
// allocations — each new preview revokes the previous one immediately.
let activePreviewUrl = null;
export function openSubmissionPdfInNewTab(args) {
  if (activePreviewUrl) URL.revokeObjectURL(activePreviewUrl);
  const doc = buildSubmissionPdf(args);
  activePreviewUrl = URL.createObjectURL(doc.output("blob"));
  window.open(activePreviewUrl, "_blank", "noopener,noreferrer");
}
